import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, LayoutDashboard, Ticket, Calendar, History, 
  LogOut, Menu, X, ChevronRight, User
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Support Tickets', href: '/tickets', icon: Ticket },
    { name: 'Bookings', href: '/bookings', icon: Calendar },
    { name: 'Service History', href: '/service-history', icon: History },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen" style={{ background: '#080B14' }}>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass h-16 flex items-center justify-between px-4 border-t-0 border-x-0 border-b border-blue-500/10">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="NetEye Logo" className="w-8 h-8 rounded-lg object-cover shadow-[0_0_10px_rgba(59,130,246,0.2)]" />
          <span className="font-semibold text-lg bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Dashboard</span>
        </Link>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-gray-300 hover:text-white"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 z-50 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{ background: '#0D1224', borderRight: '1px solid rgba(99, 130, 255, 0.12)' }}>
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-blue-500/10">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo">
            <img src="/logo.png" alt="NetEye Logo" className="w-10 h-10 rounded-xl object-cover shadow-[0_0_15px_rgba(59,130,246,0.2)]" />
            <span className="font-bold text-lg tracking-widest uppercase bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent" style={{ fontFamily: "'Exo 2', sans-serif" }}>NETEYE</span>
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                data-testid={`sidebar-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                  transition-all duration-200 border
                  ${active 
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-[inset_0_0_8px_rgba(59,130,246,0.05)]' 
                    : 'text-gray-400 border-transparent hover:text-white hover:bg-blue-500/5'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
                {active && <ChevronRight className="w-4 h-4 ml-auto text-blue-400" />}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-500/10">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-10 h-10 border border-blue-500/20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-500/20 text-blue-400">
                {user?.name?.charAt(0) || <User className="w-5 h-5" />}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-white">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            onClick={logout}
            variant="ghost" 
            className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10 hover:border-none"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
